import { useReactiveVar } from "@apollo/client";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { AnimatePresence, motion } from "framer-motion";
import size from "lodash/size";
import moment from "moment";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import SidebarSyncingError from "@apps/main/components/sidebar-syncing-error";
import SearchModal from "@apps/search/search";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  errorNotificationVar,
  leftSidePanelVar,
  mobileSidebarOpenVar,
} from "@cache/cache";
import BillingDialogCTA from "@components/billing-dialog-cta/billing-dialog-cta";
import { useLink } from "@components/link/link-helpers";
import { classNames } from "@helpers/css";
import {
  getNodeUrl,
  isCtrlSpaceEvent,
  isToggleSidebarShortcutEvent,
} from "@helpers/helpers";

import { LayoutNotification } from "./notification";
import OutdatedEditorLayout from "./outdated-editor-dialog";
import ShowSidebarButton from "./show-sidebar-button";
import LayoutAddSearch from "./sidebar-add-search";
import SidebarChatsExpanded from "./sidebar-chats-expanded";
import SidebarHijack from "./sidebar-hijack";
import SidebarMeetings from "./sidebar-meetings";
import SidebarNavigation from "./sidebar-navigation";
import SidebarOnboarding from "./sidebar-onboarding";
import SidebarReportsExpanded from "./sidebar-reports-expanded";
import SidebarSettings from "./sidebar-settings";
import SidebarSettingsExpanded from "./sidebar-settings-expanded";
import SidebarSyncingCalendar from "./sidebar-syncing-calendar";
import SidebarTabs from "./sidebar-tabs";
import TrackPreviousUrl from "./track-previous-url";

const Layout: React.FC<PropsWithChildren> = ({ children }) => {
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const leftSidePanel = useReactiveVar(leftSidePanelVar);
  const sidePanelExpanded = leftSidePanel !== false;
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const location = useLocation<TFLocationState>();
  const link = useLink();
  const mobileSidebarOpen = useReactiveVar(mobileSidebarOpenVar);
  const [isShowingSearchModal, setIsShowingSearchModal] = useState(false);
  const [sidebarSelectedDay, setSidebarSelectedDay] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const sidebarIsHidden = !uiPreferenceCache.leftNavigationSidebarExpanded;

  const handleHideSidebar = () => {
    saveUiPreference({ leftNavigationSidebarExpanded: false });
  };

  const handleCloseMobileSidebar = () => {
    mobileSidebarOpenVar(false);
  };

  const isSettingsPath = (path: string) =>
    path.startsWith("/account") ||
    path.startsWith("/settings/organization") ||
    path === "/org-chart" ||
    path.startsWith("/org-chart/") ||
    path === "/templates" ||
    path.startsWith("/templates/");

  const previousPathRef = useRef(location.pathname);
  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    previousPathRef.current = currentPath;

    // Close the mobile overlay on route change, except when first entering the
    // settings side panel from a non-settings page — there, the settings panel
    // opens within the overlay, so the overlay should stay open. Subsequent
    // navigation between settings pages still closes the overlay.
    const enteringSettingsPanel =
      isSettingsPath(currentPath) && !isSettingsPath(previousPath);
    if (!enteringSettingsPanel) {
      mobileSidebarOpenVar(false);
    }

    // Auto-open settings side panel when navigating to a settings page
    if (isSettingsPath(currentPath) && leftSidePanel !== "settings") {
      saveUiPreference({ leftNavigationSidebarExpanded: true });
      leftSidePanelVar("settings");
    }
  }, [location.pathname]);

  // Show messages coming from the back-end
  useEffect(() => {
    if (size(window.errorMessages) > 0) {
      errorNotificationVar({
        title: "Error",
        description: window.errorMessages[0],
        timeout: null,
      });
      window.errorMessages = [];
    }
  }, []);

  const handleShowSearchModal = useCallback(() => {
    if (!location.state?.background) {
      setIsShowingSearchModal(true);
    }
  }, [location]);

  const handleToggleSidebar = useCallback(() => {
    saveUiPreference({
      leftNavigationSidebarExpanded:
        !uiPreferenceCache.leftNavigationSidebarExpanded,
    });
  }, [saveUiPreference, uiPreferenceCache.leftNavigationSidebarExpanded]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (isCtrlSpaceEvent(event)) {
        handleShowSearchModal();
      }
      if (isToggleSidebarShortcutEvent(event)) {
        event.preventDefault();
        handleToggleSidebar();
      }
    },
    [handleShowSearchModal, handleToggleSidebar],
  );

  const handleChangeSidebarSelectedDay = useCallback((day: string) => {
    setSidebarSelectedDay(day);
  }, []);

  const handleHideSearchModal = useCallback(() => {
    setIsShowingSearchModal(false);
  }, []);

  const handleClickSearchModalResult = useCallback(
    (result: Parameters<typeof getNodeUrl>[0]) => {
      handleHideSearchModal();
      link.redirect(getNodeUrl(result) || "/");
    },
    [handleHideSearchModal, link],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <>
      <div className="min-h-full flex flex-1">
        {/* Prompt to refresh dialog when editor version changes. See https://github.com/Topicflow/topicflow/pull/721 */}
        <OutdatedEditorLayout />
        <TrackPreviousUrl />
        {isShowingSearchModal && (
          <SearchModal
            onClose={handleHideSearchModal}
            onClickResult={handleClickSearchModalResult}
          />
        )}

        {/* Mobile nav */}
        <Dialog
          as="div"
          open={mobileSidebarOpen}
          className="fixed inset-0 flex z-leftSidebar md:hidden tf-print-display-none"
          onClose={handleCloseMobileSidebar}
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-600/75" />

          <div className="relative flex-1 flex flex-col max-w-xs w-full pt-3 bg-gray-50">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={handleCloseMobileSidebar}
              >
                <span className="sr-only">Close sidebar</span>
                <XIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="shrink-0 flex justify-between items-center px-4">
              <SidebarSettings menuIsCollapsed={false} />
              <LayoutAddSearch
                menuIsCollapsed={false}
                onShowSearchModal={handleShowSearchModal}
              />
            </div>
            <SidebarTabs />

            <div
              className={classNames(
                "border-r flex-1 flex flex-col text-sm",
                "bg-sidenav-back",
                "overflow-y-auto scrollbar-hidden",
              )}
            >
              {/* Nav items - hidden when a side panel is expanded */}
              <AnimatePresence initial={false}>
                {!sidePanelExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="overflow-hidden shrink-0"
                  >
                    <div className={classNames("pt-2 pb-5", "px-3")}>
                      <SidebarNavigation
                        menuIsCollapsed={false}
                        onClickSearch={handleShowSearchModal}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <SidebarMeetings
                leftSidePanel={leftSidePanel}
                menuIsCollapsed={false}
                sidebarSelectedDay={sidebarSelectedDay}
                onChangeSidebarSelectedDay={setSidebarSelectedDay}
              />
              <SidebarChatsExpanded />
              <SidebarReportsExpanded />
              <SidebarSettingsExpanded />

              {/* Onboarding - hidden when a side panel is expanded */}
              {currentOrganization.featureFlags.meetings &&
                !sidePanelExpanded && (
                  <div className="pt-4 px-2 shrink-0">
                    <SidebarOnboarding currentUser={currentUser} />
                    <SidebarSyncingCalendar />
                    <SidebarSyncingError />
                  </div>
                )}
            </div>
            <SidebarHijack menuIsCollapsed={false} />
          </div>
          <div className="shrink-0 w-14"></div>
        </Dialog>

        {/* Static sidebar for desktop */}
        {!sidebarIsHidden && (
          <div
            className={classNames(
              "hidden md:flex md:flex-col md:fixed md:inset-y-0 z-10 bg-sidenav-back tf-print-display-none",
              "md:w-64 xl:w-[300px] border-r border-gray-200",
            )}
            id="sidebar"
          >
            <div
              className={classNames(
                "flex h-14 items-center shrink-0 pl-3 pr-3 gap-2 justify-between",
              )}
            >
              <SidebarSettings menuIsCollapsed={false} />
              <LayoutAddSearch
                menuIsCollapsed={false}
                onShowSearchModal={handleShowSearchModal}
                onHideSidebar={handleHideSidebar}
              />
            </div>
            <SidebarTabs />
            <div
              className={classNames(
                "flex-1 flex flex-col text-sm",
                "bg-sidenav-back",
                sidePanelExpanded
                  ? "overflow-hidden"
                  : "overflow-y-auto scrollbar-hidden",
              )}
            >
              {/* Nav items - hidden when a side panel is expanded */}
              <AnimatePresence initial={false}>
                {!sidePanelExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className="overflow-hidden shrink-0"
                  >
                    <div className="pt-2 pb-5 px-3">
                      <SidebarNavigation
                        menuIsCollapsed={false}
                        onClickSearch={handleShowSearchModal}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <SidebarMeetings
                key="meetings"
                menuIsCollapsed={false}
                sidebarSelectedDay={sidebarSelectedDay}
                onChangeSidebarSelectedDay={handleChangeSidebarSelectedDay}
                leftSidePanel={leftSidePanel}
              />
              <SidebarChatsExpanded />
              <SidebarReportsExpanded />
              <SidebarSettingsExpanded />

              {/* Onboarding - hidden when a side panel is expanded */}
              {currentOrganization.featureFlags.meetings &&
                !sidePanelExpanded && (
                  <div className="py-4 px-2 shrink-0 empty:hidden">
                    <SidebarOnboarding currentUser={currentUser} />
                    <SidebarSyncingCalendar />
                    <SidebarSyncingError />
                  </div>
                )}
            </div>
            <SidebarHijack menuIsCollapsed={false} />
          </div>
        )}

        <div
          className={classNames(
            "min-h-full flex flex-col flex-1 min-w-0 bg-content-background",
            !sidebarIsHidden && "md:pl-64 xl:pl-[300px]",
            "print:pl-0",
          )}
        >
          {!isSettingsPath(location.pathname) && (
            <div className="fixed top-3 left-5 z-sticky tf-print-display-none">
              <ShowSidebarButton />
            </div>
          )}
          {window.readOnlyMode && (
            <div className="p-3 bg-orange-300">
              Topicflow is currently in read-only mode. You may view content but
              not edit it.
            </div>
          )}

          {currentUser.hasOrgWithTooManyUsers && (
            <div className="px-6 py-2 min-h-14 flex gap-2 justify-between items-center bg-yellow-50 border-b align-middle text-sm antialiased">
              Your organization on the free tier currently has more than 10
              users.
              <BillingDialogCTA className="text-blue-link hover:underline">
                Contact us to upgrade.
              </BillingDialogCTA>
            </div>
          )}

          <main className="flex-1 flex flex-col relative">{children}</main>
        </div>
      </div>
      <LayoutNotification />
    </>
  );
};

export default Layout;
